import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layout, theme, Grid, Badge, Button, Tooltip, Space } from "antd";
import { Outlet } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAppContext } from "../context/AppContext";
import {
  getThemeSettings,
  getStoredThemeSettings,
  saveStoredThemeSettings,
} from "../services/themeService";
import PurchaseNavigation from "../navigation/PurchaseNavigationContainer";
import { useAuth } from "../context/AuthContext";
import { BellOutlined, LogoutOutlined, CalendarOutlined, ProjectOutlined } from "@ant-design/icons";
import PurchaseStageNotificationPanel from "../components/PurchaseStageNotificationPanel";
import {
  PurchaseStageNotificationProvider,
  usePurchaseStageNotifications,
  PURCHASE_STAGE_ID,
} from "../context/PurchaseStageNotificationContext";
import { permissionService } from "../services/permissionService";
import { StageMetaProvider } from "../context/StageMetaContext";
import { PurchaseOrderAccessProvider } from "../context/PurchaseOrderAccessContext";
import axiosInstance from "../config/axios";
import dayjs from "dayjs";

const { useBreakpoint } = Grid;

// Helper function to get days remaining and urgency status
const getDateStatus = (dateStr: string | null) => {
  if (!dateStr) return null;
  const targetDate = dayjs(dateStr);
  const today = dayjs();
  const daysRemaining = targetDate.diff(today, 'day');
  const weeksRemaining = Math.ceil(daysRemaining / 7);
  
  let status: 'urgent' | 'warning' | 'normal' | 'passed' = 'normal';
  let color = '#52c41a'; // green
  
  if (daysRemaining < 0) {
    status = 'passed';
    color = '#ff7875'; // muted red
  } else if (weeksRemaining <= 3) {
    status = 'urgent';
    color = '#fa8c16'; // orange
  } else if (weeksRemaining <= 6) {
    status = 'warning';
    color = '#1890ff'; // blue
  }
  
  return { daysRemaining, weeksRemaining, status, color };
};

const PurchaseLayoutInner: React.FC = () => {
  const {
    state: { isNavMenuClose },
  } = useAppContext();
  const { theme: currentTheme, updateTheme } = useTheme();
  const { user, logout } = useAuth();
  const { unreadCount, refetchNotifications } = usePurchaseStageNotifications();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isSmallDesktop = screens.md && !screens.lg;
  const isDarkMode = currentTheme.algorithm?.[0] === theme.darkAlgorithm;

  const [notificationPanelVisible, setNotificationPanelVisible] = useState(false);
  const [partyName, setPartyName] = useState<string>("");
  const [timelineData, setTimelineData] = useState<{
    purchase: string | null;
    project: string | null;
  }>({ purchase: null, project: null });

  const themeTokens = useMemo(
    () => ({
      background: isDarkMode ? "#141414" : "#f5f7fa",
      contentBackground: isDarkMode ? "#1f1f1f" : "#ffffff",
      textColor: isDarkMode
        ? "rgba(255, 255, 255, 0.85)"
        : "rgba(0, 0, 0, 0.85)",
      textColorSecondary: isDarkMode
        ? "rgba(255, 255, 255, 0.45)"
        : "rgba(0, 0, 0, 0.45)",
      borderColor: isDarkMode ? "#303030" : "#f0f0f0",
      primaryColor: currentTheme.token?.colorPrimary || "#1d8348",
    }),
    [isDarkMode, currentTheme.token?.colorPrimary]
  );

  const loadThemeFromAPI = useCallback(async () => {
    try {
      if (getStoredThemeSettings()) {
        return;
      }

      const apiSettings = await getThemeSettings();
      if (apiSettings) {
        saveStoredThemeSettings(apiSettings);
        updateTheme(apiSettings);
      }
    } catch (error) {
      console.error("Failed to load theme settings in PurchaseLayout:", error);
    }
  }, [updateTheme]);

  const loadStagePermissions = useCallback(async () => {
    const partyId = localStorage.getItem("selectedPartyId");
    let stageId = localStorage.getItem("selectedStageId");

    if (!stageId) {
      localStorage.setItem("selectedStageId", PURCHASE_STAGE_ID);
      stageId = PURCHASE_STAGE_ID;
    }

    if (!partyId || !stageId || !user?.id) {
      console.debug("Cannot load stage permissions: missing context");
      return;
    }

    try {
      await permissionService.refreshStagePermissions(stageId, user.id || "", partyId);
    } catch (error) {
      console.error("Failed to load stage permissions in PurchaseLayout:", error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!getStoredThemeSettings()) {
      loadThemeFromAPI();
    }
  }, [loadThemeFromAPI]);

  useEffect(() => {
    const storedPartyName = localStorage.getItem("selectedPartyName");
    if (storedPartyName) {
      setPartyName(storedPartyName);
    }
  }, []);

  const fetchTimelineData = useCallback(async () => {
    const partyId = localStorage.getItem("selectedPartyId");
    if (!partyId) return;

    try {
      const response = await axiosInstance.get(`/api/dashboard/party/${partyId}/timeline`);
      if (response.data?.success && response.data?.data) {
        setTimelineData({
          purchase: response.data.data.purchase,
          project: response.data.data.project,
        });
      }
    } catch (error) {
      console.debug("Failed to fetch timeline data:", error);
    }
  }, []);

  useEffect(() => {
    fetchTimelineData();
  }, [fetchTimelineData]);

  useEffect(() => {
    loadStagePermissions();
  }, [loadStagePermissions]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "selectedPartyId" || event.key === "selectedStageId") {
        loadStagePermissions();
      }
      if (event.key === "selectedPartyId") {
        fetchTimelineData();
      }
      if (event.key === "selectedPartyName") {
        setPartyName(event.newValue || "");
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [loadStagePermissions, fetchTimelineData]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      refetchNotifications();
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [refetchNotifications]);

  const layoutStyles = useMemo(
    () => ({
      backgroundColor: themeTokens.background,
      minHeight: "100vh",
      height: "100%",
      overflow: "hidden",
      display: "flex",
      color: themeTokens.textColor,
      transition: "all 0.3s ease-in-out",
    }),
    [themeTokens]
  );

  const contentStyles = useMemo(
    () => ({
      marginLeft: isMobile
        ? 0
        : isNavMenuClose
        ? 70
        : isSmallDesktop
        ? 180
        : 220,
      width: "100%",
      padding: isMobile ? "56px 16px 16px" : "0",
      overflow: "auto",
      minHeight: "100vh",
      background: themeTokens.contentBackground,
      transition: "all 0.3s ease-in-out",
      color: themeTokens.textColor,
    }),
    [themeTokens, isNavMenuClose, isMobile, isSmallDesktop]
  );

  const headerStyles = useMemo(
    () => ({
      background: "transparent",
      padding: isMobile ? "8px 12px" : "8px 16px",
      height: 52,
      lineHeight: "52px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end" as const,
      color: themeTokens.textColor,
      backdropFilter: "none",
    }),
    [isMobile, themeTokens.textColor]
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDarkMode ? "dark" : "light");
    document.body.style.backgroundColor = themeTokens.background;
    document.body.style.color = themeTokens.textColor;

    const style = document.createElement("style");
    style.textContent = `
      :root {
        color-scheme: ${isDarkMode ? "dark" : "light"};
        --primary-color: ${themeTokens.primaryColor};
        --background-color: ${themeTokens.background};
        --content-background: ${themeTokens.contentBackground};
        --text-color: ${themeTokens.textColor};
        --text-color-secondary: ${themeTokens.textColorSecondary};
        --border-color: ${themeTokens.borderColor};
      }

      .ant-card,
      .ant-modal .ant-modal-content,
      .ant-popover .ant-popover-inner,
      .ant-dropdown .ant-dropdown-menu,
      .ant-select-dropdown {
        background: var(--content-background) !important;
      }

      .ant-card-bordered,
      .ant-modal-content,
      .ant-popover-inner {
        border-color: var(--border-color) !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [isDarkMode, themeTokens]);

  const handleNotificationClick = () => {
    setNotificationPanelVisible(true);
  };

  const handleNotificationPanelClose = () => {
    setNotificationPanelVisible(false);
  };

  // Compute date statuses
  const purchaseStatus = getDateStatus(timelineData.purchase);
  const projectStatus = getDateStatus(timelineData.project);

  return (
    <Layout style={layoutStyles}>
      <PurchaseNavigation />
      <Layout style={contentStyles}>
        <Layout.Header style={headerStyles}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            width: "100%"
          }}>
            {/* Left side - Party Name */}
            <div style={{ 
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              flex: 1
            }}>
              <div style={{ 
                fontWeight: 600, 
                fontSize: isMobile ? "16px" : "23px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
                {partyName || ""}
              </div>
            </div>

            {/* Right side - Timeline Dates, Notifications, Logout, and Username */}
            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 16 }}>
              {/* Timeline Dates */}
              {!isMobile && (timelineData.purchase || timelineData.project) && (
                <Space size={16} style={{ marginRight: 8 }}>
                  {timelineData.purchase && purchaseStatus && (
                    <Tooltip title={
                      purchaseStatus.daysRemaining < 0 
                        ? `Purchase deadline passed ${Math.abs(purchaseStatus.daysRemaining)} days ago` 
                        : `${purchaseStatus.daysRemaining} days remaining`
                    }>
                      <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 6,
                        padding: "4px 12px",
                        borderRadius: 6,
                        background: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"
                      }}>
                        <CalendarOutlined style={{ color: purchaseStatus.color, fontSize: 16 }} />
                        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                          <span style={{ 
                            fontSize: 11, 
                            color: themeTokens.textColorSecondary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            Purchase Due
                          </span>
                          <span style={{ 
                            fontSize: 15, 
                            fontWeight: 600, 
                            color: purchaseStatus.color 
                          }}>
                            {dayjs(timelineData.purchase).format("DD MMM YYYY")}
                          </span>
                        </div>
                      </div>
                    </Tooltip>
                  )}
                  {timelineData.project && projectStatus && (
                    <Tooltip title={
                      projectStatus.daysRemaining < 0 
                        ? `Project deadline passed ${Math.abs(projectStatus.daysRemaining)} days ago` 
                        : `${projectStatus.daysRemaining} days remaining`
                    }>
                      <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 6,
                        padding: "4px 12px",
                        borderRadius: 6,
                        background: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"
                      }}>
                        <ProjectOutlined style={{ color: projectStatus.color, fontSize: 16 }} />
                        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                          <span style={{ 
                            fontSize: 11, 
                            color: themeTokens.textColorSecondary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            Project Due
                          </span>
                          <span style={{ 
                            fontSize: 15, 
                            fontWeight: 600, 
                            color: projectStatus.color 
                          }}>
                            {dayjs(timelineData.project).format("DD MMM YYYY")}
                          </span>
                        </div>
                      </div>
                    </Tooltip>
                  )}
                </Space>
              )}
              
              <Tooltip title="Notifications">
                <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                  <Button
                    type="text"
                    shape="circle"
                    size="small"
                    icon={<BellOutlined />}
                    onClick={handleNotificationClick}
                  />
                </Badge>
              </Tooltip>
              <Tooltip title="Logout">
                <Button
                  type="text"
                  shape="circle"
                  size="small"
                  icon={<LogoutOutlined />}
                  onClick={logout}
                />
              </Tooltip>
              <div style={{ fontWeight: 600 }}>{user?.name || "User"}</div>
            </div>
          </div>
        </Layout.Header>
        <Layout.Content>
          <Outlet />
        </Layout.Content>
      </Layout>

      <PurchaseStageNotificationPanel
        visible={notificationPanelVisible}
        onClose={handleNotificationPanelClose}
      />
    </Layout>
  );
};

const PurchaseLayout: React.FC = () => {
  return (
    <PurchaseOrderAccessProvider>
      <StageMetaProvider>
        <PurchaseStageNotificationProvider>
          <PurchaseLayoutInner />
        </PurchaseStageNotificationProvider>
      </StageMetaProvider>
    </PurchaseOrderAccessProvider>
  );
};

export default React.memo(PurchaseLayout);


